/**
 * Componente optimizado para mostrar un tile de usuario en el radar.
 * Muestra foto, nombre, estado de conexión y permite navegar al perfil.
 * Usa CachedAvatarImage para optimización de carga de imágenes.
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.user - Datos del usuario a mostrar
 * @param {string} props.user.id - ID único del usuario
 * @param {string} props.user.full_name - Nombre completo del usuario
 * @param {string} props.user.user_image - URL de la imagen de perfil
 * @param {string} props.user.online - Timestamp de última conexión
 * @param {Array} props.nearbyUsersIds - Array de IDs de usuarios cercanos
 * @param {Function} props.onOpenFriendProfile - Función para navegar al perfil de amigo
 */

import React, { useMemo, useCallback } from 'react';
import { FiMapPin } from 'react-icons/fi';
import CachedAvatarImage from '../../../components/CachedAvatarImage';

export default function UserCard({ user, nearbyUsersIds = [], onOpenFriendProfile }) {

  /**
   * Calcula si el usuario está en línea basado en su última conexión.
   * Considera "en línea" si la última conexión fue hace menos de 6 minutos.
   */
  const isOnline = useMemo(() => {
    if (!user?.online) return false;

    try {
      const lastConnection = new Date(user.online);
      const now = new Date();
      const minutesDiff = (now - lastConnection) / (1000 * 60);
      
      return minutesDiff < 6;
    } catch (error) {
      console.error(' --- UsuarioTile: Error calculating online status', error);
      return false;
    }
  }, [user?.online]);

  /**
   * Obtiene la URL de la imagen del usuario.
   * Retorna la imagen por defecto si no hay imagen disponible.
   */
  const getUserImageUrl = useCallback(() => {
    if (user?.user_image) {
      return user.user_image;
    }
    return '/default-avatar.png';
  }, [user?.user_image]);

  /**
   * Maneja la navegación al perfil del amigo.
   * Llama a la función onOpenFriendProfile si está disponible.
   */
  const handleVisitFriendProfile = useCallback(() => {
    if (!user?.id) {
      console.warn(' --- UsuarioTile: No user ID available for navigation');
      return;
    }

    console.log(' --- UsuarioTile: Navegando al perfil de amigo:', user.id);
    if (onOpenFriendProfile) {
      onOpenFriendProfile(user.id);
    } else {
      console.warn(' --- UsuarioTile: onOpenFriendProfile no está disponible');
    }
  }, [onOpenFriendProfile, user?.id]);

  /**
   * Renderiza el indicador de estado en línea.
   * Muestra un punto verde solo si el usuario está en línea.
   */
  const renderOnlineIndicator = () => {
    if (!isOnline) return null;

    return (
      <span className="w-2 h-2 bg-green-500 rounded-full mr-2" />
    );
  };

  /**
   * Renderiza la barra inferior con nombre e indicador de conexión.
   * Se superpone sobre la imagen con fondo semitransparente.
   */
  const renderUserInfoBar = () => {
    // Determinar el color de fondo basado en si hay nombre

    return (
      <div className="flex items-center absolute bottom-0 left-0 right-0 p-2 bg-neutral-950/80">
        {/* Indicador de estado en línea */}
        {renderOnlineIndicator()}
        
        {/* Nombre del usuario */}
        <span 
          className="text-white text-sm truncate flex-1"
          style={{
            maxWidth: '80%'
          }}
        >
          {user?.full_name || ''}
        </span>
        
        {/* Distancia desde ubicación actual */}
        {/* {user?.distancia_mia && (
          <span 
            className="text-gray-300 text-xs flex items-center gap-1"
          >
            <FiMapPin size={12} />
            {user.distancia_mia >= 1000 
              ? `${(user.distancia_mia / 1000).toFixed(1)}km`
              : `${Math.round(user.distancia_mia)}m`
            }
          </span>
        )} */}
      </div>
    );
  };

  // Validación de datos del usuario
  if (!user) {
    console.warn(' --- UsuarioTile: No user data provided');
    return null;
  }

  return (
    <div 
      className="relative rounded-lg overflow-hidden cursor-pointer"
      onClick={handleVisitFriendProfile}
    >
      {/* Imagen de perfil del usuario con caché */}
      <div className="aspect-square">
        <CachedAvatarImage
          userId={user?.id}
          imageUrl={getUserImageUrl()}
          nearbyUsersIds={nearbyUsersIds}
          customSize={200}
          bradius={8}
          showPlaceholder={true}
          style={{ width: '100%', height: '100%' }}
        />
      </div>
      
      {/* Barra inferior con información del usuario */}
      {renderUserInfoBar()}
    </div>
  );
}
